import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Badge, Button, Card, CardBody, Col, Container, FormGroup, Input, Label,
    Progress, Row, Spinner, Table
} from "reactstrap";
import { FaChartBar, FaArrowsRotate } from "react-icons/fa6";
import dayjs from "dayjs";
import { formatPrice } from "../../helpers/utility";
import { commercialActions } from "../../sagas/commercialSlice";

// Recharts (synthèse Top performers)
import {
    ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend
} from "recharts";

// Libellés période (cohérent avec backend)
const PERIOD_LABELS = {
    DAYS_15: "15 jours",
    DAYS_30: "30 jours",
    DAYS_45: "45 jours",
    DAYS_60: "60 jours",
    DAYS_90: "90 jours",
};

const getProgressColor = (pct) => {
    if (pct >= 100) return "success";
    if (pct >= 90) return "success";
    if (pct >= 60) return "warning";
    return "danger";
};

export default function CommercialProgressPage() {
    const dispatch = useDispatch();

    const { list: commercials, loading } = useSelector((s) => s.commercials);
    const { performanceById, perfLoading } = useSelector((s) => s.commercials);

    const [filters, setFilters] = useState({
        period: "DAYS_30",
        refDate: dayjs().format("YYYY-MM-DD"),
    });

    // Charger commerciaux + perf
    useEffect(() => {
        if (!commercials || commercials.length === 0) {
            dispatch(commercialActions.list({}));
        }
    }, []);

    useEffect(() => {
        dispatch(commercialActions.loadPerformance({ period: filters.period, refDate: filters.refDate }));
    }, [filters.period, filters.refDate]);

    // Dataset pour tableau
    const rows = useMemo(() => {
        if (!commercials) return [];
        return commercials.map((c) => {
            const p = performanceById?.[c.id];
            const objective = Number(p?.objective || 0);
            const baseTotal = Number(p?.baseTotal || 0);
            const pct = objective > 0 ? Math.min(100, Math.round((baseTotal / objective) * 100)) : 0;
            return {
                id: c.id,
                code: c.code,
                fullName: c.fullName,
                phone: c.phone,
                contractType: c.contractType,
                plan: p?.plan || null,
                objective,
                baseTotal,
                pct,
                totalCommission: Number(p?.totalCommission || 0),
                baseUsed: p?.baseUsed || "-",
            };
        });
    }, [commercials, performanceById]);

    // Synthèse Top performers (POSTED vs preview? Ici: estimation “preview/perf”)
    const chartData = useMemo(() => {
        // Tri par total commission estimée (desc)
        const sorted = [...rows].sort((a, b) => b.totalCommission - a.totalCommission).slice(0, 10);
        return sorted.map(r => ({
            commercial: r.fullName || r.code,
            commission: r.totalCommission,
        }));
    }, [rows]);

    const isBusy = loading || perfLoading;

    return (
        <Container fluid className="mt-3">
            <Row className="mb-3">
                <Col>
                    <h4 className="mb-0">Progression des commerciaux</h4>
                    <div className="text-muted">Aperçu des objectifs vs réalisations et commission estimée sur la période sélectionnée.</div>
                </Col>
            </Row>

            {/* Filtres */}
            <Card className="shadow-sm mb-3">
                <CardBody>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <FormGroup className="mb-0">
                                <Label>Période</Label>
                                <Input
                                    type="select"
                                    value={filters.period}
                                    onChange={(e) => setFilters((f) => ({ ...f, period: e.target.value }))}
                                >
                                    {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup className="mb-0">
                                <Label>Date de référence</Label>
                                <Input
                                    type="date"
                                    value={filters.refDate}
                                    onChange={(e) => setFilters((f) => ({ ...f, refDate: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <span className="text-muted small">
                                Fenêtre calculée côté serveur selon la période (ex: {PERIOD_LABELS[filters.period]} jusqu’au {filters.refDate}).
                            </span>
                        </Col>
                        <Col md={3} className="d-flex align-items-end justify-content-end">
                            <Button
                                color="secondary"
                                onClick={() => dispatch(commercialActions.loadPerformance({ period: filters.period, refDate: filters.refDate }))}
                                disabled={isBusy}
                            >
                                {isBusy ? <Spinner size="sm" /> : <><FaChartBar className="me-2" />Rafraîchir</>}
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Graphique Top performers */}
            {chartData.length > 0 && (
                <Card className="shadow-sm mb-3">
                    <CardBody>
                        <div className="d-flex align-items-center mb-2">
                            <FaChartBar className="text-primary me-2" />
                            <h5 className="mb-0">Top performers — Commission estimée</h5>
                        </div>
                        <div style={{ width: "100%", height: 320 }}>
                            <ResponsiveContainer>
                                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="commercial"
                                        interval={0}
                                        angle={-20}
                                        textAnchor="end"
                                        height={70}
                                    />
                                    <YAxis
                                        tickFormatter={(v) =>
                                            v >= 1000000
                                                ? (v / 1000000).toFixed(1) + "M"
                                                : v >= 1000
                                                    ? (v / 1000).toFixed(0) + "k"
                                                    : v
                                        }
                                        width={70}
                                        tick={{ fontSize: 12, fill: "#555" }}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatPrice(value)}
                                        labelFormatter={(label) => `Commercial: ${label}`}
                                    />
                                    <Legend />
                                    <Bar dataKey="commission" name="Commission estimée" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <small className="text-muted">
                            Estimations calculées via l’endpoint batch de performance (non POSTED). Change en temps réel selon les ventes.
                        </small>
                    </CardBody>
                </Card>
            )}

            {/* Tableau progression */}
            <Card className="shadow-sm">
                <CardBody>
                    {isBusy ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Chargement…</div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Commercial</th>
                                        <th>Plan</th>
                                        <th>Objectif</th>
                                        <th>Réalisé (base)</th>
                                        <th>Progression</th>
                                        <th>Commission estimée</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length > 0 ? rows.map((r, idx) => (
                                        <tr key={r.id}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <div className="fw-semibold">{r.fullName}</div>
                                                <div className="text-muted small">{r.phone || "-"}</div>
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                {r.plan ? (
                                                    <>
                                                        <Badge color="secondary" className="me-1">
                                                            {r.plan.period} • {r.plan.baseType === "REVENUE" ? "CA" : "Marge"}
                                                        </Badge>
                                                        <div className="text-muted small">Base: {r.baseUsed}</div>
                                                    </>
                                                ) : <span className="text-muted">—</span>}
                                            </td>
                                            <td>{formatPrice(r.objective)}</td>
                                            <td>{formatPrice(r.baseTotal)}</td>
                                            <td style={{ minWidth: 200 }}>
                                                <Progress value={r.pct} color={getProgressColor(r.pct)}>
                                                    {r.pct}%
                                                </Progress>
                                            </td>
                                            <td><strong>{formatPrice(r.totalCommission)}</strong></td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-4">Aucun commercial trouvé</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
}
